import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../utils/authStorage';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import Remarks2 from './Remarks2';
import Remarks__list from './Remarks__list';
import { filterRemarksList } from '../../../../Custom/Custom';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';

const getAuthConfig = () => {
    if (!hasAuthSession()) return null;
    return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

function PartA() {
    const navigate = useNavigate();

    // State for header data
    const [headerData, setHeaderData] = useState({
        despatchSlNo: '',
        permanentSlNo: '',
        companyName: '',
        dslNo: '',
        surveyYear: ''
    });

    // State for table data
    const [tableData, setTableData] = useState([]);

    // State for Remarks section
    const [remarksData, setRemarksData] = useState({
        remarksBlock: '',
        remarksItems: '',
        remarks: ''
    });
    const [remarksList, setRemarksList] = useState([]);

    // Handle textarea change for "If high or low" column
    const handleIfHighOrLowChange = (index, value) => {
        const updatedData = [...tableData];
        updatedData[index].ifHighOrLow = value;
        setTableData(updatedData);
    };

    // Use ref to track if data has been loaded to prevent multiple API calls
    const dataLoadedRef = useRef(false);
    const isLoadingRef = useRef(false);
    const apiCallMadeRef = useRef(false);

    // Function to fetch data from API
    const fetchExistingData = useCallback(async (dslNo, surveyYear) => {
        if (!dslNo || !surveyYear) {
            console.log('DSL No or Survey Year not available for fetching data');
            dataLoadedRef.current = true;
            return;
        }

        console.log('Starting API call for getInputItemsHAndI with DSL:', dslNo, 'Year:', surveyYear);
        showLoading(true);
        try {
            const currentConfig = getAuthConfig();
            if (!currentConfig) {
                console.error('Authentication token not found for fetching data');
                showLoading(false);
                dataLoadedRef.current = true;
                return;
            }

            // Extract only year part (e.g., "2024" from "2024-25")
            const yearOnly = surveyYear ? surveyYear.split('-')[0] : surveyYear;

            const payload = {
                dsl_no: dslNo,
                survey_year: yearOnly
            };

            console.log('Fetching existing data with payload:', payload);

            const response = await axios.post(`${API_URL}api/input-items-indigenous/InputItemsHAndIPartA`, payload, currentConfig);

            if (response.data && response.data.status && Array.isArray(response.data.data)) {
                console.log('Data fetched successfully:', response.data.data);

                // Map API data to table format with category headers
                const apiData = response.data.data;
                const finalRows = [];

                // Helper function to safely convert values
                const safeString = (val) => (val === null || val === undefined ? '' : String(val));

                // Separate items by block (H and I)
                const hItems = [];
                const iItems = [];

                apiData.forEach((row) => {
                    const block = safeString(row.block_no || row.block || '').toUpperCase();
                    const itemRow = {
                        slNo: safeString(row.sl_no || ''),
                        blockNo: block,
                        item: `${safeString(row.item_code || '') ? safeString(row.item_code) + '-' : ''}${safeString(row.item_desc || '')}`.trim(), // Sequential number will be added later
                        unit: (() => {
                            const u = safeString(row.unit_of_qty);
                            return u === '0' ? '' : u;
                        })(),
                        averageRate: safeString(row.average_rate_per_unit || ''),
                        ifHighOrLow: safeString(row.if_high_or_low || row.reasons || '')
                    };
                    if (block === 'I') {
                        iItems.push(itemRow);
                    } else {
                        hItems.push(itemRow);
                    }
                });

                // Calculate sequential numbers
                let sequentialSlNo = 1; // Start from 1
                let categoryNumber = 1; // Category header number (1, 2, etc.)

                // Add category header and items for Block H
                if (hItems.length > 0) {
                    const blockHStartSlNo = sequentialSlNo; // Store starting serial number for Block H
                    finalRows.push({
                        slNo: '',
                        blockNo: '',
                        item: 'Input items (Indigenous) Major Ten basic items consumed',
                        unit: 'X',
                        averageRate: 'X',
                        ifHighOrLow: '',
                        isCategoryHeader: true,
                        categoryBlock: 'H',
                        itemCount: hItems.length,
                        categoryStartSlNo: blockHStartSlNo, // Store for display in merged cell
                        categoryNumber: categoryNumber++ // Category number: 1
                    });
                    hItems.forEach((item, index) => {
                        const currentSlNo = sequentialSlNo++;
                        finalRows.push({
                            ...item,
                            slNo: index === 0 ? String(blockHStartSlNo) : '', // First item shows category start number
                            blockNo: index === 0 ? item.blockNo : '',
                            item: `${currentSlNo}) ${item.item}`, // Add sequential number to item description
                            isCategoryHeader: false,
                            categoryBlock: 'H',
                            sequentialSlNo: currentSlNo // Store sequential number for item description
                        });
                    });
                }

                // Add category header and items for Block I
                // Reset sequential numbering to start from 1 for Block I
                if (iItems.length > 0) {
                    sequentialSlNo = 1; // Reset to 1 for Block I
                    const blockIStartSlNo = sequentialSlNo; // Starting serial number for Block I (reset to 1)
                    finalRows.push({
                        slNo: '',
                        blockNo: '',
                        item: 'Directly imported item consumed (major five items)',
                        unit: 'X',
                        averageRate: 'X',
                        ifHighOrLow: '',
                        isCategoryHeader: true,
                        categoryBlock: 'I',
                        itemCount: iItems.length,
                        categoryStartSlNo: blockIStartSlNo, // Store for display in merged cell
                        categoryNumber: categoryNumber++ // Category number: 2
                    });
                    iItems.forEach((item, index) => {
                        const currentSlNo = sequentialSlNo++;
                        finalRows.push({
                            ...item,
                            slNo: index === 0 ? String(blockIStartSlNo) : '', // First item shows category start number
                            blockNo: index === 0 ? item.blockNo : '',
                            item: `${currentSlNo}) ${item.item}`, // Add sequential number to item description
                            isCategoryHeader: false,
                            categoryBlock: 'I',
                            sequentialSlNo: currentSlNo // Store sequential number for item description
                        });
                    });
                }

                setTableData(finalRows);

                // Map remarks data from API response if available
                let mappedRemarksList = [];
                if (response.data.remarks && Array.isArray(response.data.remarks)) {
                    mappedRemarksList = response.data.remarks.map((remark, index) => {
                        // Extract block letter from "BLOCK H" or "BLOCK I" format
                        // Filter out invalid values like "PART A"
                        let blockLetter = '';
                        if (remark.block) {
                            const blockValue = String(remark.block).trim();
                            // Only process valid blocks (H, I) and ignore invalid ones like "PART A"
                            if (blockValue && !blockValue.toUpperCase().includes('PART')) {
                                // Handle formats like "BLOCK H", "BLOCK-I", "H", etc.
                                blockLetter = blockValue.replace(/^BLOCK\s*[-]?\s*/i, '').trim().toUpperCase();
                                // Ensure it's only H or I
                                if (blockLetter !== 'H' && blockLetter !== 'I') {
                                    blockLetter = '';
                                }
                            }
                        }

                        // Format date for display
                        let formattedDate = '';
                        const dateValue = remark.createdDate || remark.created_date || remark.updatedDate || remark.updated_date || '';
                        if (dateValue) {
                            try {
                                const date = new Date(dateValue);
                                if (!isNaN(date.getTime())) {
                                    formattedDate = date.toLocaleString('en-IN', {
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    });
                                }
                            } catch (e) {
                                formattedDate = dateValue;
                            }
                        }

                        return {
                            slNo: index + 1,
                            blocks: blockLetter || remark.block || '',
                            columns: remark.columnsName || remark.columns_name || remark.columns || '',
                            items: remark.items || remark.Items || '',
                            remarks: remark.remarks || '',
                            remarksCreatedOn: formattedDate || dateValue || '',
                            remarksBy: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || '',
                            role: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || ''
                        };
                    });
                    setRemarksList(mappedRemarksList);
                    console.log('Mapped remarks list:', mappedRemarksList);
                } else {
                    setRemarksList([]);
                }

                // Set default remarksData - Block to "ALL" by default to show all remarks
                setRemarksData({
                    remarksBlock: '',
                    remarksItems: '',
                    remarks: ''
                });

                dataLoadedRef.current = true;
                console.log('API data loaded successfully, dataLoadedRef set to true');
            } else {
                console.log('No data found or invalid response');
                setTableData([]);
                dataLoadedRef.current = true;
                console.log('No data found, initialized with empty array, dataLoadedRef set to true');
            }
        } catch (error) {
            console.error('Error fetching data from API:', error);
            console.error('Error Response:', error.response);
            console.error('Error Status:', error.response?.status);
            console.error('Error Data:', error.response?.data);

            if (error.response?.status === 404) {
                console.log('No existing data found, initializing with empty array');
                setTableData([]);
            } else {
                toast.error('Failed to load existing data.', messages.COLOR_RED);
                setTableData([]);
            }
            dataLoadedRef.current = true;
            console.log('Error occurred, initialized with empty array, dataLoadedRef set to true');
        } finally {
            showLoading(false);
        }
    }, []);

    // Memoize the fetch function to prevent recreation
    const fetchHeaderData = useCallback(async () => {
        // Prevent multiple API calls using refs
        if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
            console.log('Data already loaded, loading in progress, or API call already made - skipping');
            return;
        }

        try {
            const balanceSheetData = localStorage.getItem('balanceSheetFormData');

            if (balanceSheetData) {
                const parsedData = JSON.parse(balanceSheetData);

                const headerInfo = {
                    despatchSlNo: parsedData.dslNo || '',
                    permanentSlNo: parsedData.pslNo || '',
                    companyName: parsedData.nameUnit || '',
                    dslNo: parsedData.dslNo || '',
                    surveyYear: parsedData.year || ''
                };

                setHeaderData(headerInfo);

                // Fetch existing data if DSL No and Survey Year are available
                if (headerInfo.dslNo && headerInfo.surveyYear) {
                    apiCallMadeRef.current = true; // Mark API call as made
                    isLoadingRef.current = true;
                    await fetchExistingData(headerInfo.dslNo, headerInfo.surveyYear);
                    isLoadingRef.current = false;
                } else {
                    setTableData([]);
                }
            } else {
                console.log('No balanceSheet data found in localStorage');
                setHeaderData({
                    despatchSlNo: 'DSL No. not set',
                    permanentSlNo: 'PSL No. not set',
                    companyName: 'Company name not set',
                    dslNo: '',
                    surveyYear: ''
                });
                setTableData([]);
            }
        } catch (error) {
            console.error('Error fetching data from localStorage:', error);
            setHeaderData({
                despatchSlNo: 'Error loading data',
                permanentSlNo: 'Error loading data',
                companyName: 'Error loading data',
                dslNo: '',
                surveyYear: ''
            });
            setTableData([]);
        }
    }, [fetchExistingData]);

    // Fetch header data from localStorage when component mounts
    useEffect(() => {
        fetchHeaderData();
    }, [fetchHeaderData]);

    // Handle reset function
    const handleReset = () => {
        const updatedData = tableData.map(row => ({
            ...row,
            ifHighOrLow: ''
        }));
        setTableData(updatedData);
        setRemarksData({
            remarksBlock: '',
            remarksItems: '',
            remarks: ''
        });
    };

    // Function to save data to API
    const handleSaveToAPI = async () => {
        if (!headerData.dslNo || headerData.dslNo === 'DSL No. not set') {
            toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
            return false;
        }

        showLoading(true);

        try {
            const currentConfig = getAuthConfig();
            if (!currentConfig) {
                toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
                showLoading(false);
                return false;
            }

            // Extract year only from survey year (e.g., "2024-25" -> "2024")
            const yearOnly = headerData.surveyYear ? headerData.surveyYear.split('-')[0] : headerData.surveyYear || '2024';

            // Prepare data for API - only item rows (excluding category headers)
            const dataToSave = [];
            tableData.forEach((row, index) => {
                if (!row.isCategoryHeader && row.item && row.item.trim() !== '') {
                    // Extract item code from item description (format: "1) 3413901-2-ethyl hexanol")
                    const itemMatch = row.item.match(/^\d+\)\s*(\d+)-/);
                    const itemCode = itemMatch ? itemMatch[1] : '';

                    dataToSave.push({
                        dsl_no: headerData.dslNo,
                        survey_year: yearOnly,
                        block_no: row.categoryBlock || '',
                        item_code: itemCode,
                        item_desc: row.item.replace(/^\d+\)\s*\d+-/, '').trim(),
                        unit_of_qty: row.unit || '',
                        average_rate_per_unit: row.averageRate || '',
                        if_high_or_low: row.ifHighOrLow || ''
                    });
                }
            });

            if (dataToSave.length === 0) {
                toast.warning('No data to save. Please fill at least one row.', messages.COLOR_RED);
                showLoading(false);
                return false;
            }

            // Prepare remarks array
            const remarksArray = [];
            if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
                // Extract block letter from remarksBlock (e.g., "Block H" -> "H", "Block I" -> "I")
                // Only accept valid blocks: "Block H", "Block I", "H", "I", or "ALL"
                let blockLetter = '';
                const validBlocks = ['Block H', 'Block I', 'H', 'I', 'ALL'];
                const selectedBlock = remarksData.remarksBlock || '';
                
                // Validate that the selected block is one of the valid options
                if (selectedBlock && validBlocks.includes(selectedBlock)) {
                    if (selectedBlock !== 'ALL') {
                        const blockMatch = selectedBlock.match(/Block\s*([HI])/i);
                        if (blockMatch) {
                            blockLetter = blockMatch[1].toUpperCase();
                        } else if (selectedBlock === 'H' || selectedBlock === 'I') {
                            blockLetter = selectedBlock.toUpperCase();
                        }
                    }
                } else if (selectedBlock && !validBlocks.includes(selectedBlock)) {
                    // If invalid block is selected, skip adding remarks or show error
                    console.warn('Invalid block selected:', selectedBlock);
                    toast.error('Please select a valid Block (Block H or Block I)', messages.COLOR_RED);
                    showLoading(false);
                    return false;
                }

                remarksArray.push({
                    dslNo: headerData.dslNo.toString(),
                    surveyYear: yearOnly,
                    block: blockLetter || '',
                    columnsName: '', // Column name should be empty for Part A (no column concept)
                    items: remarksData.remarksItems || '', // Item name from Items dropdown
                    remarks: remarksData.remarks || ''
                });
            }

            // Structure payload with data and remarks arrays
            const finalPayload = {
                data: dataToSave,
                remarks: remarksArray
            };

            console.log('Saving data to API:', finalPayload);

            const response = await axios.post(`${API_URL}api/input-items-indigenous/InputItemsHAndIPartA`, finalPayload, currentConfig);

            if (response.data && response.data.status) {
                toast.success('Data saved successfully!', messages.COLOR_RED);

                // Mark page as saved for green header
                markPageAsSaved(PAGE_NAMES.PART_A);
                // Refresh balance sheet validation to update header colors
                if (headerData.dslNo && headerData.surveyYear) {
                  await refreshBalanceSheetValidation(headerData.dslNo, headerData.surveyYear);
                }

                return true;
            } else {
                toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
                return false;
            }

        } catch (error) {
            console.error('API Error Details:', error);
            console.error('Error Response:', error.response);
            console.error('Error Status:', error.response?.status);
            console.error('Error Data:', error.response?.data);

            if (error.response?.status === 403) {
                toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
            } else if (error.response?.status === 401) {
                toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
            } else if (error.response?.status === 404) {
                toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
            } else if (error.response?.status >= 500) {
                toast.error('Server error. Please try again later.', messages.COLOR_RED);
            } else {
                toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
            }
            return false;
        } finally {
            showLoading(false);
        }
    };

    return (
        <div className="container-fluid py-3">
            {/* Header Section */}
            <HeaderInfo
                title="Part A"
                icon="fa fa-file-text"
                iconColor="#1976d2"
                titleColor="#1976d2"
                backgroundColor=""
                dispatchSlNo={headerData.despatchSlNo}
                permanentSlNo={headerData.permanentSlNo}
                companyName={headerData.companyName}
                companyPlaceholder="Enter Company Name"
                showBlockCType={false}
                includeTableStyles={false}
            />

            {/* Main Content Area */}
            <div className="card mb-3">
                <div className="card-body">
                    <div className="row">
                        <div className="col-12">
                            <div className="table-responsive">
                                <table className="table table-bordered mb-0">
                                    <thead>
                                        <tr>
                                            <th>Sl. No.</th>
                                            <th>Bl No.</th>
                                            <th>Item</th>
                                            <th>Unit</th>
                                            <th>Average rate per Unit</th>
                                            <th>If high or low, reason to be furnished by</th>
                                        </tr>
                                        <tr>
                                            <th style={{ textAlign: 'center' }}>(1)</th>
                                            <th style={{ textAlign: 'center' }}>(2)</th>
                                            <th style={{ textAlign: 'center' }}>(3)</th>
                                            <th style={{ textAlign: 'center' }}>(4)</th>
                                            <th style={{ textAlign: 'center' }}>(5)</th>
                                            <th style={{ textAlign: 'center' }}>(6)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tableData.length > 0 ? (
                                            tableData.map((row, index) => {
                                                // Check if this is a category header
                                                const isCategoryHeader = row.isCategoryHeader;

                                                // Find the next category header index to calculate rowspan
                                                let nextCategoryIndex = -1;
                                                if (isCategoryHeader) {
                                                    for (let i = index + 1; i < tableData.length; i++) {
                                                        if (tableData[i].isCategoryHeader) {
                                                            nextCategoryIndex = i;
                                                            break;
                                                        }
                                                    }
                                                    // If no next category found, rowspan goes to end
                                                    if (nextCategoryIndex === -1) {
                                                        nextCategoryIndex = tableData.length;
                                                    }
                                                }

                                                // Calculate rowspan: from current category header to next category header (or end)
                                                const rowspan = isCategoryHeader ? (nextCategoryIndex - index) : null;

                                                // Check if this is the first item after a category header
                                                const isFirstItemAfterHeader = !isCategoryHeader &&
                                                    index > 0 &&
                                                    tableData[index - 1]?.isCategoryHeader;

                                                // Get the first item's data for merged cells
                                                const firstItemData = isCategoryHeader && index + 1 < tableData.length
                                                    ? tableData[index + 1]
                                                    : null;

                                                return (
                                                    <tr key={index}>
                                                        {isCategoryHeader ? (
                                                            // Category header row - merge Sl. No. and Bl No. columns
                                                            <>
                                                                <td
                                                                    rowSpan={rowspan}
                                                                    style={{
                                                                        textAlign: 'center',
                                                                        verticalAlign: 'middle'
                                                                    }}
                                                                >
                                                                    {row.categoryNumber || ''}
                                                                </td>
                                                                <td
                                                                    rowSpan={rowspan}
                                                                    style={{
                                                                        textAlign: 'center',
                                                                        verticalAlign: 'middle'
                                                                    }}
                                                                >
                                                                    {row.categoryBlock || ''}
                                                                </td>
                                                                <td style={{ fontWeight: 'bold' }}>
                                                                    {row.item}
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <span style={{ color: '#dc3545', fontSize: '18px' }}>✕</span>
                                                                </td>
                                                                <td style={{ textAlign: 'center' }}>
                                                                    <span style={{ color: '#dc3545', fontSize: '18px' }}>✕</span>
                                                                </td>
                                                                <td></td>
                                                            </>
                                                        ) : (
                                                            // Item row - don't render Sl. No. and Bl No. cells (they're merged in category header)
                                                            <>
                                                                <td>{row.item}</td>
                                                                <td style={{ textAlign: 'center' }}>{row.unit}</td>
                                                                <td style={{ textAlign: 'center' }}>{row.averageRate}</td>
                                                                <td>
                                                                    <textarea
                                                                        className="form-control"
                                                                        rows="2"
                                                                        value={row.ifHighOrLow}
                                                                        placeholder="Enter reasons..."
                                                                        style={{ minWidth: '100%', resize: 'vertical' }}
                                                                        onChange={(e) => handleIfHighOrLowChange(index, e.target.value)}
                                                                    />
                                                                </td>
                                                            </>
                                                        )}
                                                    </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan="6" style={{ textAlign: 'center', padding: '20px' }}>
                                                    No data available
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Remarks Section */}
            <div className='row'>
                <div className='col-md-12 remarks2-section'>
                    <Remarks2
                        remarksData={remarksData}
                        setRemarksData={setRemarksData}
                        blockOptions={['Block H', 'Block I']}
                        itemOptions={useMemo(() => {
                            // Extract items from tableData (excluding category headers)
                            const items = [];
                            tableData.forEach(row => {
                                if (!row.isCategoryHeader && row.item && row.item.trim() !== '') {
                                    items.push(row.item);
                                }
                            });
                            return items;
                        }, [tableData])}
                        remarksList={remarksList}
                    />
                </div>
            </div>

             {/* Action Buttons */}
             <div className="row">
                <div className="col-12">
                    <hr />
                </div>
                <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
                    <button
                        type="button"
                        className="btn btn_orange mx-1"
                    >
                        Print
                        <i className="fa fa-print ms-1"></i>
                    </button>
                    <button
                        type="button"
                        className="btn btn_orange mx-1"
                        onClick={handleSaveToAPI}
                    >
                        Save
                    </button>
                    <button
                        type="reset"
                        className="btn btn_orange mx-1"
                        onClick={handleReset}
                    >
                        Reset
                    </button>
                    <button
                        type="button"
                        className="btn btn_orange mx-1"
                        onClick={() => navigate('/asiScheduleMainMenu')}
                    >
                        Cancel
                    </button>
                </div>
                <div className="col-md-4 text-center text-md-end">
                    <button
                        type="button"
                        className="btn btn_orange mx-1"
                        onClick={() => navigate('/ict-usage-energy-conservation')}
                    >
                        <i className="fa fa-angle-left me-1"></i>
                        Previous
                    </button>
                    <button
                        type="button"
                        className="btn btn_orange mx-1"
                        onClick={async () => {
                            // Save data first, then navigate only if successful
                            const saveSuccess = await handleSaveToAPI();
                            if (saveSuccess) {
                                navigate('/net-profit-tally');
                            }
                        }}
                    >
                        Next
                        <i className="fa fa-angle-right ms-1"></i>
                    </button>
                </div>
            </div>

            {/* Remarks List Section */}
            {(() => {
                const filteredRemarksList = filterRemarksList(remarksList, remarksData);
                return <div className=''><Remarks__list remarksData={filteredRemarksList} /></div>;
            })()}

           

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
        </div>
    );
}

export default PartA